import React from "react";
import ReactDOM from "react-dom";
import MessageBox from "./messagebox";
import HcsFlyoutShoppingBasket from "./controls/hcs-fsb";
import { TrackingTypes } from "./tools/tracking-base";

/**
 * @class
 * @classdesc Handle Cart control which includes "Add to Basket" button and price display.
 * 
 * @param {object} paramObj - Parameters for initialization.
 * @param {object} paramObj.hcs - HcS instance
 * @param {string} paramObj.currency - Currency of current user
 * @param {array} paramObj.availablePriceTypes - List of available price types for the current user.
 * @param {string} paramObj.selectedPriceType - Price type of current user.
 */
export default class HcsCart
{
    /**
     * Read thumbnail specs from template.
     * @returns {object} Thumbnail size and color specifications.
     */
    get thumbnailSpecs() {
        return {
            width: this.hcs.api.instance(".hcs-thumbnail-template").clientWidth,
            height: this.hcs.api.instance(".hcs-thumbnail-template").clientHeight,
            bgColor: this.hcs.api.rgbaToColorCode(window.getComputedStyle(this.hcs.api.instance(".hcs-thumbnail-template"), null).getPropertyValue("background-color")).replace("#", "0x")
        };
    }

    constructor(paramObj) {
        let self = this;

        this.hcs = paramObj.hcs;
        this.container = this.hcs.container.querySelector(".hcs-cart-container");

        this.currency = paramObj.currency;

        this.show();

        this.instance(".hcs-toggle-button").addEventListener("click", () => {
            if(self.container.classList.contains("hcs-expand")) {
                self.expand(false);
            } else {
                self.expand(true);
            }
        });

        this.priceTypeSelector = ReactDOM.render(React.createElement(HcsFlyoutShoppingBasket, {
            hcs: this.hcs,
            priceType: paramObj.selectedPriceType,
            availablePriceTypes: paramObj.availablePriceTypes,
            priceTypeChanged: (priceType) => self.priceTypeChanged.call(self, priceType)
        }), this.instance(".hcs-fsb-select-container"));

        this.lock();

        this._addToBasket = this._addToBasket.bind(this);
    }

    get globalEnabled() {
        return this.hcs.settings.get("SETTINGS.SHOP_READ_PRICE") !== "0";
    }

    priceTypeChanged(priceType) {
        this.priceType = priceType;

        this.updatePrice();
    }

    /**
     * Make cart control visible.
     */
    show() {
        this.toggleState("hcs-hidden", !this.globalEnabled);
    }

    /**
     * Hide cart control.
     * This makes the control completely invisible
     * hiding the currency button as well.
     */
    hide() {
        this.toggleState("hcs-hidden", true);
    }

    /**
     * Expand/collapse cart control.
     * @param {boolean} [show=true] - Toggle cart control.
     */
    expand(show) {
        if(arguments.length < 1 || show === true) {
            this.toggleState("hcs-expand", true);
        } else {
            this.toggleState("hcs-expand", false);
        }
    }

    /**
     * Lock this control.
     * Main content (price, price type selector and "add to cart" button) will be hidden
     * only the currency button stays visible, with no effect when the user clicks it.
     * @param {boolean} [lock=true] - Lock cart control.
     */
    lock(lock) {
        if(arguments.length < 1 || lock === true) {
            this.toggleState("hcs-locked", true);
        } else {
            this.toggleState("hcs-locked", false);
        }
    }

    /**
     * Toggle CSS class state by adding or removing a class.
     * @param {string} className - Name of the CSS class to be set or removed.
     * @param {bool} [toggle] - "true" to set class, "false" to remove. When this
     * parameter is not set it returns if the given class is currently set or not.
     * @returns {bool} Availability of given class name when "toggle" parameter is not specified.
     */
    toggleState(className, toggle) {
        if(arguments.length > 1) {
            if(toggle) {
                this.container.classList.add(className);
            } else {
                this.container.classList.remove(className);
            }
        } else {
            return this.container.classList.contains(className);
        }
    }

    /**
     * Read current currency.
     * @returns {string} Currency value.
     */
    get currency() {
        return this._currency;
    }

    /**
     * Set new currency.
     * @param {string} value - New currency value.
     */
    set currency(value) {
        this._currency = value;
    }

    /**
     * Read current priceType.
     * @returns {string} Current price type.
     */
    get priceType() {
        return this.priceTypeSelector.priceType;
    }

    /**
     * Set new price type.
     * @param {string} value - New price type.
     */
    set priceType(value) {
        this.priceTypeSelector.priceType = value;
    }

    /**
     * Change total price to given value.
     * Currency symbol will be added automatically.
     * @param {float|string} value - Price value. This value can be either numeric or just a text.
     * In caser of a number the value will be handeled as price and will be automatically formatted.
     */
    setPrice(value) {
        this.instance(".hcs-price-value").innerHTML = isNaN(value) ? value : this.formatPrice(this.hcs.api.round(value, 2));
    }

    /**
     * Format price value depending on currency and culture.
     * @param {float} value - Price value.
     * @returns {string} Formatted price.
     */
    formatPrice(value) {
        return parseFloat(value).toLocaleString(this.hcs.constants.cultureId, { minimumFractionDigits: 2 }) + " " + this.currency;
    }

    /**
     * Read current total price from ECS.
     */
    updatePrice() {
        if(!this.globalEnabled) {
            return;
        }

        let self = this;

        this.hcs.partsList.getData({ showSpinner: false }).then((result) => {
            let price = result.totalPrice;

            // Hide price, price type selector and "add to cart" button when there are no products.
            this.lock(result.items.length > 0 ? false : true);

            //! -- ShopInterface should deliver prices for VARIATIONARTICLES
            /*let varProduct = result.items.find(item => item.articleType === "VARIATIONARTICLE");

            // When there is at least one variation product show no price but the "price on request" text.
            if(varProduct) {
                price = self.hcs.t.gui("Cart_Price_Prefix") + " " + varProduct.availabilityStatusMessage;
                this.toggleState("hcs-price-on-request", true);
            } else {
                this.toggleState("hcs-price-on-request", false);
            }*/

            self.setPrice(price);
        }).catch(errorResult => {
            self.hcs.api.showError({
                title: "Could not update price",
                message: errorResult.error
            });
        });
    }

    createBomData() {
        let self = this;

        return new Promise((resolve, reject) => {
            self.hcs.partsList.getData({
                priceRequest: false,
                logRequest: true
            }).then((partsListResult) => {
                let ecsObject = JSON.parse(partsListResult.ecs);

                let bomData = {
                    lineItems: []
                };

                if(ecsObject.oBomData && ecsObject.oBomData.aGroupedDatasets && ecsObject.oBomData.aGroupedDatasets[0] && ecsObject.oBomData.aGroupedDatasets[0].aDatasets) {
                    // Build basic object for cart.
                    bomData.lineItems = ecsObject.oBomData.aGroupedDatasets[0].aDatasets.map((bomItem, index) => {
                        return {
                            rowIndex: index,
                            articleNumber: bomItem.sArtNr,
                            articleType: partsListResult.items[index].articleType,
                            quantity: bomItem.nQuantity,
                            consignment: self.hcs.settings.get("SETTINGS.CART_BASKET_FIELD_CONSIGNMENT"),
                            requestedDeliveryDate: null,
                            infoText: "",
                            createdBy: self.hcs.configurator,
                            itemSource: self.hcs.settings.get("SETTINGS.CART_BASKET_FIELD_ITEM_SOURCE"),
                            variationAttributesList: partsListResult.items[index].variationAttributesList,
                            configurationDetails: partsListResult.items[index].configurationDetails,
                            shopPosNumber: index
                        };
                    });

                    resolve(bomData);
                } else {
                    reject({
                        type: "nodata",
                        result: null
                    });
                }
            }).catch(result => {
                reject({
                    type: "error",
                    result: result
                });
            });
        });
    }

    /**
     * Add current product to (external) cart.
     * To access the external cart function the property "SHOP_ADD_TO_BASKET" must contain
     * the client-side JavaScript function.
     * @param {object} [options] - Additional options
     * @param {function} [success] - Function being called when configuration has been successfully added to basket.
     */
    addToBasket(options) {
        if(!this.hcs.api.checkFunction("addToBasket")) {
            return;
        }

        this.hcs.api.logToolbarOption("Add to Basket");

        // We wait until (running) ECS callback has finished.
        // This is necessary because:
        // When the user enters the last required value inside a text field and immediately presses the "Next" button,
        // the blur event of the text field triggers the ECS callback, but the button "Next" event tells us that the current
        // configuration step has not yet been finished. This requires that the user has to press "Next" a second time.
        //
        // To avoid this we first wait for the text field callback, then we check if the current configuration step is ready to finish.
        // If not we show the warning message, otherwise we trigger the "Next" callback.
        window.cadclick.hcs[this.hcs.configurator].renderer.configWait(["setCfgValue", "setSubCfgValue"], this).then(() => {
            let commission = window.cadclick.hcs[this.hcs.configurator].renderer.configurationData
                .items.find(item => item.attributes.find(attr => attr.ident === "A_CART_COMMISSION"))?.attributes.find(attr => attr.ident === "A_CART_COMMISSION").info.value;

            let quantity = window.cadclick.hcs[this.hcs.configurator].renderer.configurationData
                .items.find(item => item.attributes.find(attr => attr.ident === "A_CART_AMOUNT"))?.attributes.find(attr => attr.ident === "A_CART_AMOUNT").info.value;

            this._addToBasket(Object.assign({}, {
                commission: commission,
                quantity: quantity
            }, options));
        });
    }

    /**
     * Main function "addToBasket".
     * The function above is only a "portal" which first checks if this operation is allowed.
     * Also ensures that possible running ECS callbacks are finished.
     */
    _addToBasket(options) {
        let self = this;

        let opts = Object.assign({}, {
            quantity: 1,
            commission: null,
            success: function() { },
            error: function() { }
        }, options);

        let loading = true;

        setTimeout(() => {
            if(loading) {
                self.hcs.api.progressBar.progress("indeterminate");
            }
        }, 200);

        let basketAddSucess = function(basketAddResponse, cartData, shopData) {
            try {
                self.hcs.api.callback({
                    url: self.hcs.api.getUrl(`log/shop/${self.hcs.configurationId}/addToCart`),
                    showErrorMessage: false,
                    data: {
                        jsonIn: JSON.stringify(cartData),
                        jsonOut: JSON.stringify(basketAddResponse || {})
                    },
                    method: "POST"
                });
            } catch(e) { }

            opts.success.call(self, basketAddResponse);

            loading = false;
            self.hcs.api.progressBar.progress(false);

            if(shopData) {
                let basketList = document.createElement("ul");
                basketList.className = "basket-list";

                let basketUiItemsHtml = cartData.lineItems.map(lineItem => {
                    let shopDataItem = shopData.items.find(i => i.productId.replace(/\./g, "") === lineItem.articleNumber.replace(/\./g, ""));

                    let template = BasketItemTemplate.replace("$$THUMBNAIL$$", shopDataItem ? shopDataItem.thumbnail :
                        `<img src="${self.hcs.api.getUrl("ConfigImage/icons/thumbs", lineItem.articleNumber.replace(/\./g, "")) + `.jpg?size=${self.thumbnailSpecs.width}x${self.thumbnailSpecs.height}&bgcolor=${self.thumbnailSpecs.bgColor}`}" />`);
                    template = template.replace("$$DESCRIPTION$$", shopDataItem ? shopDataItem.displayName : "");
                    template = template.replace("$$ARTNUM$$", shopDataItem ? shopDataItem.productId : lineItem.articleNumber);
                    template = template.replace("$$COUNT$$", lineItem.quantity.toString() + (shopDataItem ? shopDataItem.unit : ""));

                    return template;
                });

                basketList.innerHTML = basketUiItemsHtml.join("");

                self.hcs.api.messageBox.show({
                    modal: true,
                    title: self.hcs.t.gui("AddToBasketSuccessList"),
                    text: basketList,
                    messageType: null,
                    classNames: "hcs-add-basket-confirm",
                    titleCloseButton: true,
                    buttons: MessageBox.MB_OK | MessageBox.MB_CANCEL,
                    buttonTexts: {
                        "ok": self.hcs.t.gui("AddToBasketSuccessList_Button_ToBasket"),
                        "cancel": self.hcs.t.gui("AddToBasketSuccessList_Button_Continue"),
                    },
                    buttonClassCategories: {
                        "ok": "hcs-secondary"
                    },
                    onOk: function(event) {
                        this.close();
                        //alert("TODO: GO TO HAEFELE CART PAGE");
                        let cartUrl = [];
                        cartUrl.push(window.location.origin);
                        cartUrl.push(window.location.pathname.split('/')[1]);
                        cartUrl.push("cart");
                        cartUrl.push("");
                        window.location.href = cartUrl.join('/');
                    },
                    onCancel: function() {
                        this.close();
                        self.hcs.api.saveConfiguration({
                            saveAsCopy: true,
                            loadNewConfiguration: true,
                            configurationName: "shop.basketCopyNew",
                            showConfirmDialog: false,
                            success: function(result) {
                                self.hcs.api.loadConfiguration(result.configurationId).then(_ => {
                                    self.hcs.renderer.changeGroup("FIRST");
                                });
                            }
                        });
                    }
                });
            } else {
                self.hcs.api.messageBox.show({
                    modal: true,
                    text: self.hcs.t.gui("AddToBasketSuccess"),
                    buttons: MessageBox.MB_OK,
                    messageType: MessageBox.MB_INFO
                });
            }
        };

        this.createBomData().then(bomData => {
            loading = false;
            self.hcs.api.progressBar.progress(false);

            self.hcs.tracker.track(TrackingTypes.Shop, {
                optionId: "Add to Basket",
                productIds: bomData.lineItems.map(item => item.articleNumber)
            });

            // Change quantity and consignment of overall BoM
            bomData.lineItems.forEach(item => {
                if(opts.quantity) {
                    item.quantity *= opts.quantity;
                }

                if(opts.commission) {
                    item.consignment = opts.commission;
                }
            });

            let allowAttributesFixed = self.hcs.settings.get("SETTINGS.SHOP_SET_ATTRIBUTES_FIXED", "bool");
            let sendConfigId = self.hcs.settings.get("SETTINGS.SHOP_SEND_CONFIGURATION_ID", "bool");

            let cartData = {
                lineItems: bomData.lineItems.map(item => {
                    var res = {};

                    res.articleNumber = item.articleNumber;
                    res.quantity = item.quantity;
                    if(sendConfigId) { res.configurationId = self.hcs.configurationId; }
                    res.consignment = item.consignment;
                    res.requestedDeliveryDate = item.requestedDeliveryDate;
                    res.infoText = item.infoText;
                    res.createdBy = item.createdBy;
                    res.configurationAttributesFixed = allowAttributesFixed ? item.articleType === "VARIATIONARTICLE" : false;
                    res.itemSource = item.itemSource;
                    res.variationAttributesList = item.variationAttributesList;
                    /*res.shopPosNumber = item.shopPosNumber;*/

                    return res;
                })
            };

            this.hcs.api.saveConfiguration({
                sendToShop: false,
                showConfirmDialog: false,
                configurationName: "shop.addToBasket",
                success: _ => {
                    loading = true;

                    setTimeout(() => {
                        if(loading) {
                            self.hcs.api.progressBar.progress("indeterminate");
                        }
                    }, 200);

                    self.hcs.externalFunction("SETTINGS.SHOP_ADD_TO_BASKET", cartData).then((basketAddResponse) => {
                        self.hcs.partsList.getData({ priceRequest: false }).then(shopData => {
                            basketAddSucess(basketAddResponse, cartData, shopData);
                        }).catch(error => {
                            console.error("Could not evaluate shop product info");
                            console.error(error);

                            basketAddSucess(basketAddResponse, cartData, null);
                        });
                    }).catch(e => {
                        loading = false;
                        self.hcs.api.progressBar.progress(false);
                        let eText = self.hcs.t.gui("AddToBasketFailed");

                        if(e && e.statusText) {
                            eText += "<br />" + e.statusText;
                        }

                        opts.error.call(self, eText);

                        try {
                            self.hcs.api.callback({
                                url: self.hcs.api.getUrl(`log/shop/${self.hcs.configurationId}/addToCart`),
                                showErrorMessage: false,
                                data: {
                                    jsonIn: JSON.stringify(cartData),
                                    jsonOut: e.statusText
                                },
                                method: "POST"
                            });
                        } catch(e) { }

                        self.hcs.api.messageBox.show({
                            modal: true,
                            text: eText,
                            buttons: MessageBox.MB_OK,
                            messageType: MessageBox.MB_ERROR
                        });
                    });
                }
            });
        }).catch(result => {
            loading = false;
            self.hcs.api.progressBar.progress(false);

            switch(result.type) {
                case "nodata":
                    self.hcs.api.messageBox.show({
                        modal: true,
                        title: self.hcs.t.gui("AddToBomNoData_Title"),
                        text: self.hcs.t.gui("AddToBomNoData_Message"),
                        buttons: MessageBox.MB_OK,
                        messageType: MessageBox.MB_INFO
                    });
                    break;

                default:
                    console.error(result);
                    break;
            }
        });
    }

    instance(selector) {
        return this.hcs.api.instance(selector, this.container);
    }
}

const BasketItemTemplate = `<li>
        <div class="hcs-tni">$$THUMBNAIL$$</div>
        <div>
            <div class="hcs-desc">$$DESCRIPTION$$</div>
            <div>
                <div class="hcs-pnum">$$ARTNUM$$</div>
                <div class="hcs-count">$$COUNT$$</div>
            </div>
        </div>
    </li>`;